import dayjs from 'dayjs';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { useLazyGetClientsFilterDataQuery } from '@app/api/Clients/api';
import { CLIENTS_FILTER, FRONT_WARNING } from '@app/constants';
import {
  initialClientsFilter,
  initialFilterGroups,
  initialVisitsFilter,
} from '@app/data';
import { useMemoizeObject, useUserPrint } from '@app/hooks';
import {
  CHF_BIRTHDAY,
  CHF_PERIOD,
  CHF_VISITS_FILTER,
} from '@app/pages/Clients/ClientsList/constants/filterKeysAndIds';
import { initialFiltersConfig } from '@app/pages/Clients/ClientsList/data/initialFiltersConfig';
import { ClientFiltersContext } from '@app/providers/ClientFiltersProvider/ClientFiltersContext';
import type { NClientFiltersProvider } from '@app/providers/ClientFiltersProvider/ClientFiltersProvider.types';
import { NotificationContext } from '@app/providers/NotificationProvider';
import type { NClientServices } from '@app/types';
import type { NClients } from '@app/types/Clients';

import {
  buildDataTree,
  getClientLogicalGroupIndex,
  getFiltersArray,
} from '@pages/Clients/ClientsList/utils';

import type { NClientsList } from '@pages/Clients/ClientsList/ClientsList.types';

import type { DataNode } from 'antd/es/tree';

const dateFormatList = ['DD.MM.YYYY', 'YYYY-MM-DD'];
const defaultTagIncludingStatuses = [true];

const filtersStorageKey = 'client-filters';
const tagsStorageKey = `${filtersStorageKey}-tags-including`;
const searchTermStorageKey = `${filtersStorageKey}-search-term`;

const generateInitialFilterGroups = (
  serverFiltersFromStorage?: string | null
): Array<NClientFiltersProvider.FilterGroups> => {
  let localInitialFilterGroups = initialFilterGroups;

  if (!filtersStorageKey || !serverFiltersFromStorage)
    return initialFilterGroups;

  const serverFiltersFromStorageInJSON: NClientsList.Filters = JSON.parse(
    serverFiltersFromStorage
  );
  const { VisitsFilter } = serverFiltersFromStorageInJSON;

  localInitialFilterGroups = (
    VisitsFilter.filter(
      ({ FilterGroupsId }) => typeof FilterGroupsId !== undefined
    ) || []
  ).map(({ FilterGroupsId: id, IsAnd: isAnd }, index) => {
    const areLastFilterGroups = index + 1 === VisitsFilter.length;

    return {
      id,
      isAnd,
      areLastFilterGroups,
    };
    // Здесь "as" применяется потому, что выше через Array#filter() мы убедились,
    // что идентификатор группы не может поступать на вход пустым
  }) as Array<Omit<NClientFiltersProvider.FilterGroups, 'id'> & { id: string }>;

  return localInitialFilterGroups;
};

const getClientTagsIncluding: NClientFiltersProvider.State['getClientTagsIncluding'] =
  (filterGroupIndex, tagIncludingStatuses) => {
    const clientGroupIndex = getClientLogicalGroupIndex(filterGroupIndex);
    const includingStatus = tagIncludingStatuses[clientGroupIndex];
    const isIncludingStatusExisted = includingStatus !== undefined;

    if (!isIncludingStatusExisted) return true;

    return includingStatus;
  };

export const ClientFiltersProvider: NClientFiltersProvider.Main = ({
  children,
}) => {
  const serverFiltersFromStorage = sessionStorage.getItem(filtersStorageKey);
  const tagsIncludingFromStorage = sessionStorage.getItem(tagsStorageKey);
  const searchTermFromStorage = sessionStorage.getItem(searchTermStorageKey);

  const { activeBranchId } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);
  const [filters, setFilters] = useState<NClientsList.Filters>(
    serverFiltersFromStorage
      ? JSON.parse(serverFiltersFromStorage)
      : initialFiltersConfig
  );
  const [serverFilters, setServerFilters] = useState<NClientsList.Filters>(
    serverFiltersFromStorage
      ? JSON.parse(serverFiltersFromStorage)
      : initialFiltersConfig
  );
  const [searchTerm, setSearchTerm] = useState(searchTermFromStorage || '');
  const [todayDate] = useState(() => new Date().toISOString().split('T')[0]);

  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>(
    (filters.VisitsFilter?.[0]?.Values?.[0]?.ServiceIds?.map(
      (item: NClientServices.Service['Id']) => `child-${item}`
    ) as React.Key[]) || []
  );
  const [allCategoriesAndServices, setAllCategoriesAndServices] = useState<
    DataNode[]
  >([]);
  const [searchedCategoriesAndServices, setSearchedCategoriesAndServices] =
    useState<DataNode[]>([]);
  const [searchedCategories, setSearchedCategories] = useState<DataNode[]>([]);
  const [allTags, setAllTags] = useState<DataNode[]>([]);
  const [allRecordTags, setAllRecordTags] = useState<DataNode[]>([]);
  const [recordSearchedTags, setRecordSearchedTags] = useState<DataNode[]>([]);
  const [allTagsWithoutAdaptation, setAllTagsWithoutAdaptation] = useState<
    NClients.ModelClientsFilterData['ClientTags']
  >([]);
  const [allRecordTagsWithoutAdaptation, setAllRecordTagsWithoutAdaptation] =
    useState<NClients.ModelClientsFilterData['RecordTags']>([]);
  const [searchedTags, setSearchedTags] = useState<DataNode[]>([]);
  const [allEmployees, setAllEmployees] = useState<
    NClients.ModelClientsFilterData['Employees']
  >([]);
  const [filterGroups, setFilterGroups] = useState<
    Array<NClientFiltersProvider.FilterGroups>
  >(generateInitialFilterGroups(serverFiltersFromStorage));
  const [tagIncludingStatuses, setTagIncludingStatuses] = useState<boolean[]>(
    tagsIncludingFromStorage
      ? JSON.parse(tagsIncludingFromStorage)
      : [...defaultTagIncludingStatuses]
  );

  const [specificFilterId, setSpecificFilterId] = useState<string | null>(null);

  const memoizedServerFilters =
    useMemoizeObject(serverFilters) || initialFiltersConfig;

  const [getClientsFilterData] = useLazyGetClientsFilterDataQuery();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (activeBranchId) {
          const {
            CategoryServices,
            Services,
            ClientTags,
            Employees,
            RecordTags,
          } = await getClientsFilterData({
            ActiveBranchId: activeBranchId,
          }).unwrap();
          // Данные для фильтра услуг
          setAllCategoriesAndServices(
            buildDataTree(CategoryServices, Services)
          );
          setSearchedCategoriesAndServices(
            buildDataTree(CategoryServices, Services)
          );

          // Данные для фильтра категорий услуг
          setSearchedCategories(
            buildDataTree(CategoryServices, Services, true)
          );

          // Данные для фильтра тегов
          setAllTags(buildDataTree(ClientTags, [], true));
          setSearchedTags(buildDataTree(ClientTags, [], true));
          setAllTagsWithoutAdaptation(ClientTags);
          setAllRecordTags(buildDataTree(RecordTags, [], true));
          setRecordSearchedTags(buildDataTree(RecordTags, [], true));
          setAllRecordTagsWithoutAdaptation(RecordTags);

          // Данные для фильтра исполнителей
          setAllEmployees(Employees);
        }
      } catch (error) {
        showNotification(error as Error);
      }
    };

    fetchData();
  }, [activeBranchId, getClientsFilterData, showNotification]);

  useEffect(() => {
    sessionStorage.setItem(filtersStorageKey, JSON.stringify(serverFilters));
  }, [serverFilters]);

  useEffect(() => {
    sessionStorage.setItem(
      tagsStorageKey,
      JSON.stringify(tagIncludingStatuses)
    );
  }, [tagIncludingStatuses]);

  useEffect(() => {
    sessionStorage.setItem(searchTermStorageKey, searchTerm);
  }, [searchTerm]);

  const handleRangeChange = useCallback<
    NClientFiltersProvider.State['handleRangeChange']
  >(
    (filterUnitMaxKey, value, filterGroupIndex) => {
      const handleCount = () => {
        if (value === null) {
          return null;
        } else {
          return Number(value);
        }
      };

      setFilters({
        ...filters,
        VisitsFilter: [
          ...(filters['VisitsFilter'].filter(
            (item, index: number) => index !== filterGroupIndex
          ) || []),
          {
            Values: [
              {
                ...filters.VisitsFilter?.[filterGroupIndex || 0]?.Values?.[0],
                [filterUnitMaxKey]: handleCount(),
              },
            ],
          },
        ],
      });
    },
    [filters]
  );

  const clearVisitsGroup = useCallback(
    (groupIndex?: number) => {
      const updatedVisitsFilter: NClientsList.Filters['VisitsFilter'] =
        filters.VisitsFilter.map((filter, index) =>
          index === (groupIndex || 0)
            ? {
                ...filter,
                Values: initialVisitsFilter?.[0]?.Values,
              }
            : filter
        );

      setFilters((prev) => ({
        ...prev,
        VisitsFilter: updatedVisitsFilter,
      }));
      setServerFilters((prev) => ({
        ...prev,
        VisitsFilter: updatedVisitsFilter,
      }));

      setCheckedKeys([]);
    },
    [filters]
  );

  const clearGroupOneTagArray = (
    filters: NClientsList.Filters,
    filterGroupIndex: number
  ) => {
    const filtersByClients: NClientsList.Filters['ClientsFilter'] =
      getFiltersArray<NClientsList.Filters['ClientsFilter']>({
        ...filters[CLIENTS_FILTER],
        [filterGroupIndex - 1]: {
          ...filters[CLIENTS_FILTER][filterGroupIndex - 1],
          Values: [
            {
              ...filters?.[CLIENTS_FILTER]?.[filterGroupIndex - 1]?.Values?.[0],
              TagIds: null,
            },
          ],
        },
        [filterGroupIndex]: {
          ...filters[CLIENTS_FILTER][filterGroupIndex],
          Values: [
            {
              ...filters?.[CLIENTS_FILTER]?.[filterGroupIndex]?.Values?.[0],
              TagIds: null,
            },
          ],
        },
      });

    return {
      ...filters,
      [CLIENTS_FILTER]: filtersByClients,
    };
  };

  const prepareTagsIncludingChange: NClientFiltersProvider.PrepareTagsIncludingChange =
    useCallback(
      (globalFilterGroupsId) => {
        // Здесь завязка идет на VisitsFilter для простоты подсчетов,
        // т.к. ClientsFilter имеет 2 объекта на 1 (одну) логическую группу
        const activeLogicalGroup = filters.VisitsFilter.find(
          ({ FilterGroupsId: localFilterGroupsId }) =>
            localFilterGroupsId === globalFilterGroupsId
        );
        const activeLogicalGroupIndex = activeLogicalGroup
          ? filters[CHF_VISITS_FILTER].indexOf(activeLogicalGroup)
          : null;
        const hasActiveLogicalGroupIndex =
          !!activeLogicalGroupIndex || activeLogicalGroupIndex === 0;

        if (!hasActiveLogicalGroupIndex)
          return showNotification(
            'Информация по логическим группам фильтров не найдена',
            FRONT_WARNING
          );

        return activeLogicalGroupIndex;
      },
      [filters, showNotification]
    );

  const resetTagsIncludingStatus: NClientFiltersProvider.ChangeTagsIncludingStatus =
    useCallback(
      (globalFilterGroupsId) => {
        const activeLogicalGroupIndex =
          prepareTagsIncludingChange(globalFilterGroupsId);

        if (!activeLogicalGroupIndex && activeLogicalGroupIndex !== 0) return;

        const statusesWithoutActiveGroup = [...tagIncludingStatuses];

        statusesWithoutActiveGroup[activeLogicalGroupIndex] = true;
        setTagIncludingStatuses(statusesWithoutActiveGroup);
      },
      [prepareTagsIncludingChange, tagIncludingStatuses]
    );

  const deleteTagsIncludingStatus: NClientFiltersProvider.ChangeTagsIncludingStatus =
    useCallback(
      (globalFilterGroupsId) => {
        const activeLogicalGroupIndex =
          prepareTagsIncludingChange(globalFilterGroupsId);

        if (!activeLogicalGroupIndex && activeLogicalGroupIndex !== 0) return;

        const statusesWithoutActiveGroup = tagIncludingStatuses.filter(
          (item, index) => index !== activeLogicalGroupIndex
        );

        setTagIncludingStatuses(statusesWithoutActiveGroup);
      },
      [tagIncludingStatuses, prepareTagsIncludingChange]
    );

  const clearGroupBothTagArrays = useCallback(
    (filterGroupIndex: number) => {
      const filterGroupsId =
        filters.ClientsFilter[filterGroupIndex]?.FilterGroupsId;

      if (!filterGroupsId)
        return showNotification(
          'Идентификатор логической группы фильтров не найден',
          FRONT_WARNING
        );

      resetTagsIncludingStatus(filterGroupsId);

      setFilters((prev) => clearGroupOneTagArray(prev, filterGroupIndex));
      setServerFilters((prev) => clearGroupOneTagArray(prev, filterGroupIndex));
    },
    [filters.ClientsFilter, showNotification, resetTagsIncludingStatus]
  );

  const clearClientsGroup: NClientsList.ClearClientsGroup = useCallback(
    (filterGroupIndex) => {
      // Общая чистка тегов для всех фильтров группы
      setFilters({
        ...filters,
        ClientsFilter:
          initialClientsFilter as NClientsList.Filters['ClientsFilter'],
      });
      setServerFilters({
        ...serverFilters,
        ClientsFilter:
          initialClientsFilter as NClientsList.Filters['ClientsFilter'],
      });

      // Чистка только фильтра клиентских тегов
      const [defaultIncludingStatus] = defaultTagIncludingStatuses;
      const tagIncludingStatusesCopy = [...tagIncludingStatuses];

      tagIncludingStatusesCopy[getClientLogicalGroupIndex(filterGroupIndex)] =
        defaultIncludingStatus;
      setTagIncludingStatuses(tagIncludingStatusesCopy);

      clearGroupBothTagArrays(filterGroupIndex);
    },
    [filters, serverFilters, tagIncludingStatuses, clearGroupBothTagArrays]
  );

  const setUpServerDataChunk = useCallback<
    NClientFiltersProvider.State['setUpServerDataChunk']
  >(
    (
      filterGroupKey,
      filterUnitKey,
      isRange,
      filterUnitMaxKey,
      filterGroupIndex,
      isCalendar = false
    ) => {
      const currentFilterGroupIndex = filterGroupIndex || 0;
      const currentFilterGroup =
        filters[filterGroupKey][currentFilterGroupIndex] || {};
      const currentFilterGroupValues = currentFilterGroup?.Values?.[0] || {};
      const currentServerFilterGroup =
        serverFilters[filterGroupKey][currentFilterGroupIndex] || {};
      const chosenValue = currentFilterGroupValues?.[filterUnitKey];
      const chosenMaxValue =
        isRange && filterUnitMaxKey
          ? currentFilterGroupValues?.[filterUnitMaxKey]
          : undefined;

      const newValues = {
        ...currentServerFilterGroup?.Values?.[0],
        ...(isCalendar
          ? {
              DateStart: currentFilterGroupValues?.DateStart,
              DateEnd: currentFilterGroupValues?.DateEnd,
            }
          : isRange
          ? {
              [filterUnitKey]: chosenValue,
              [filterUnitMaxKey!]: chosenMaxValue,
            }
          : { [filterUnitKey]: chosenValue }),
      };

      const updatedServerFilters = serverFilters[filterGroupKey].map(
        (filter, index) =>
          index === currentFilterGroupIndex
            ? { ...filter, Values: [newValues] }
            : filter
      );

      setServerFilters((prev) => ({
        ...prev,
        [filterGroupKey]: updatedServerFilters,
      }));
    },
    [filters, serverFilters]
  );

  const handleDisabling = useCallback<
    NClientFiltersProvider.State['handleDisabling']
  >(
    (
      filterGroupKey,
      filterUnitKey,
      isRange,
      filterUnitMaxKey,
      filterGroupIndex = 0
    ) => {
      const group = filters?.[filterGroupKey]?.[filterGroupIndex];
      const values = group?.Values?.[0];

      if (!values) return true;

      const value = values?.[filterUnitKey] ?? null;

      if (isRange && filterUnitMaxKey) {
        const maxValue = values?.[filterUnitMaxKey] ?? null;
        return value === null && maxValue === null;
      }

      const startDate = values?.DateStart ?? null;
      const endDate = values?.DateEnd ?? null;
      const isBirthDateFilter =
        filterUnitKey === CHF_PERIOD || filterUnitKey === CHF_BIRTHDAY;

      if (isBirthDateFilter && startDate && endDate) {
        return false;
      }

      return value === null;
    },
    [filters]
  );

  const updateFilterUnit: NClientFiltersProvider.UpdateFilterUnit = useCallback(
    (filters, index, filterUnitKey, value, filterType) => {
      const currentFilter = filters[filterType][index];
      const currentFilterValuesObj =
        currentFilter?.Values?.length > 0 ? currentFilter?.Values?.[0] : {};

      const newCurrentFilterValuesObj = {
        ...currentFilterValuesObj,
        [filterUnitKey]: value,
      };

      const Values = [newCurrentFilterValuesObj];

      if (filterType === 'VisitsFilter') {
        const updatedCurrentFilter: NClientsList.VisitsFilterUnit = {
          ...currentFilter,
          Values: Values as NClientsList.VisitsFilterUnitValue[],
        };
        const updatedFilter = [...filters[filterType]];

        updatedFilter.splice(index, 1, updatedCurrentFilter);

        return updatedFilter;
      } else {
        const updatedCurrentFilter: NClientsList.ClientsFilterUnit = {
          ...currentFilter,
          Values: Values as NClientsList.ClientsFilterUnitValue[],
        };
        const updatedFilter = [...filters[filterType]];

        updatedFilter.splice(index, 1, updatedCurrentFilter);

        return updatedFilter;
      }
    },
    []
  );

  const onChangeFiltersHandler: NClientFiltersProvider.OnChangeFiltersHandler =
    useCallback(
      (filterType, filterGroupIndex, filterUnitKey, value) => {
        setFilters((prevFilters: NClientsList.Filters) => {
          const updatedFilters = {
            ...prevFilters,
            [filterType]: updateFilterUnit(
              prevFilters,
              filterGroupIndex,
              filterUnitKey,
              value,
              filterType
            ),
          };

          return updatedFilters;
        });
      },
      [updateFilterUnit]
    );

  return (
    <ClientFiltersContext.Provider
      value={{
        filters,
        todayDate,
        initialDate: dayjs(todayDate, dateFormatList[1]),
        dateFormatList,
        checkedKeys,
        allCategoriesAndServices,
        searchedCategoriesAndServices,
        searchedCategories,
        allTags,
        searchedTags,
        allTagsWithoutAdaptation,
        allEmployees,
        serverFilters,
        filterGroups,
        specificFilterId,
        tagIncludingStatuses,
        defaultTagIncludingStatuses,
        tagsStorageKey,
        memoizedServerFilters,
        searchTerm,
        setSearchTerm,
        setTagIncludingStatuses,
        setSpecificFilterId,
        setFilterGroups,
        handleDisabling,
        setUpServerDataChunk,
        setServerFilters,
        clearVisitsGroup,
        clearClientsGroup,
        setAllEmployees,
        setAllTagsWithoutAdaptation,
        setSearchedTags,
        setAllTags,
        setSearchedCategories,
        setSearchedCategoriesAndServices,
        setAllCategoriesAndServices,
        setCheckedKeys,
        setFilters,
        handleRangeChange,
        onChangeFiltersHandler,
        getClientTagsIncluding,
        clearGroupBothTagArrays,
        deleteTagsIncludingStatus,
        allRecordTags,
        recordSearchedTags,
        allRecordTagsWithoutAdaptation,
        setAllRecordTags,
        setRecordSearchedTags,
      }}
    >
      {children}
    </ClientFiltersContext.Provider>
  );
};
